/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 09:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import LoadingBar from 'react-top-loading-bar';
import Select, { components } from 'react-select';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Classnames from 'classnames';
import * as sass from 'sass';
import {
  reduxForm,
  getFormSyncErrors,
  FormProps,
  change,
  reset,
  getFormValues,
  initialize
} from 'redux-form';
import {
  ButtonGroup,
  FullscreenBar,
  Button,
  Icon,
  Badge,
  Frame,
  Spinner,
  Tooltip
} from '@shopify/polaris';
import { CaretDownMinor, DesktopMajor, ExitMajor, MobileMajor } from '@shopify/polaris-icons';
import queryString from 'query-string';

import LeftPanel from './menu-settings';
import Preview from './preview';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import Error404 from 'src/assets/images/shopify-integration/404.svg';
import IconFullscreen from 'src/assets/images/theme-setting/fullscreen.svg?react';

import {
  enterSABFullscreen,
  exitSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';
import { confirmLeavePage, getShop } from 'src/helpers';
import { routes } from 'src/routes';
import {
  DEVICE,
  PREVIEW_PAGES_SHOPIFY_INTEGRATION,
  VIEW_AS_OPTIONS_BY_THEME,
  SIDEBAR_MENU
} from 'src/constants/theme-settings';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

import { handleOpenFullscreen } from 'actions/app-integration-actions';
import {
  convertToDataThemeSetting,
  convertToShopifyThemeSetting,
  drill
  // ensureDataTranslation
} from 'src/helpers/shopify-integration-settings-helper';
import * as ShopifyIntegrationSettingsAction from 'src/stores/actions/shopify-integration/shopify-integration-settings-actions';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as shopifyIntegrationThemeAction from 'actions/shopify-integration/theme-actions';
import * as templateInstanceAction from 'actions/shopify-integration/template-instance-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';
import { trackFSEvent_ChangeSSRVisualEditor } from 'src/integrations/fullStory';
import { KEY_EDITOR_STATE } from 'src/constants/shopify-integration';
import { getPrevRoute, removePrevRoute } from 'src/helpers/session-storage-helper';

type PropsFromState = {
  synchError: Object,
  editThemeSettingLoading: boolean
};

type PropsFromDispatch = {
  changeSelectedPage: Function,
  handleOpenFullscreen: Function,
  updateField: Function,
  resetForm: Function,
  getFilterData: Function,
  changeDevice: Function
};

type ComposedProps = PropsFromState & PropsFromDispatch & FormProps & WithI18nProps;

class ShopifyIntegrationSettingsVisualEditor extends Component<ComposedProps> {
  constructor(props) {
    super(props);
    this.state = {
      activePopoverMenu: false,
      isErrorTheme: false,
      compiledSettingsCss: '',
      prevDevice: props.device
    };
    this.loadingBar = null;
    this.isActiveOtherLanguage = false; // Default "en" -> different from default -> change to true
    this.unsubscribeExit = undefined;
    this.editorModel = {};
  }

  componentDidMount() {
    const { changeSelectedPage, getFilterData, i18n, location, themes, getAllThemes } = this.props;
    const queries = queryString.parse(location.search);
    const { page } = queries;

    this.getTemplateSetting();
    if (!themes.length) {
      getAllThemes();
    }

    // default collection page
    let selectedPage = {
      ...PREVIEW_PAGES_SHOPIFY_INTEGRATION[0],
      label: i18n.translate(PREVIEW_PAGES_SHOPIFY_INTEGRATION[0].label)
    };
    if (page === 'search') {
      selectedPage = {
        ...PREVIEW_PAGES_SHOPIFY_INTEGRATION[1],
        label: i18n.translate(PREVIEW_PAGES_SHOPIFY_INTEGRATION[1].label)
      };
    }
    changeSelectedPage(selectedPage);

    this.handleOpenEditor();

    getFilterData();

    Emitter.on(EVENT_KEY.LOADING_PREVIEW_THEME_SETTING, this.handleLoadingPreview);
    Emitter.on(EVENT_KEY.COMPLETE_PREVIEW_THEME_SETTING, this.handleCompleteLoadingPreview);

    document.documentElement.style.overflow = 'hidden';
    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    const { i18n, changeSelectedPage, themeSetting, widgetSettings, scssContent } = nextProps;

    // Compare changed themeSetting to refresh translating i18n locale
    if (this.props.themeSetting !== themeSetting && this.isActiveOtherLanguage) {
      this.isActiveOtherLanguage = false; // Reset to false to prevent infinite rendering
    }

    if (this.props.i18n.locale !== i18n.locale) {
      this.isActiveOtherLanguage = true;
      changeSelectedPage({
        ...PREVIEW_PAGES_SHOPIFY_INTEGRATION[0],
        label: i18n.translate(PREVIEW_PAGES_SHOPIFY_INTEGRATION[0].label)
      });
    }

    if (
      !_.isEmpty(widgetSettings) &&
      !_.isEmpty(scssContent) &&
      _.isEmpty(this.state.compiledSettingsCss)
    ) {
      this.compileScss(widgetSettings, scssContent);
    }
  }

  componentWillUnmount() {
    const { resetForm, updateMultiFieldsReducer, updateMultiFieldsThemeIntegrationReducer } =
      this.props;
    resetForm();
    updateMultiFieldsReducer({
      labelTranslation: {},
      activeMenuItem: '',
      templateSettingLoading: null,
      themeSettingDefaultValue: null,
      previewData: {},
      sideBarMenu: SIDEBAR_MENU.STYLE,
      device: DEVICE.DESKTOP
    });

    updateMultiFieldsThemeIntegrationReducer({
      widgetSettings: {},
      scssContent: '',
      translations: {}
    });

    Emitter.off(EVENT_KEY.LOADING_PREVIEW_THEME_SETTING, this.handleLoadingPreview);
    Emitter.off(EVENT_KEY.COMPLETE_PREVIEW_THEME_SETTING, this.handleCompleteLoadingPreview);

    document.documentElement.style.overflow = 'auto';

    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
    window.localStorage.removeItem(`${getShop()}-${KEY_EDITOR_STATE}`);
    this.unsubscribeExit();
  }

  async getTemplateSetting() {
    const {
      getTemplateSettingById,
      getThemeSettingDefaultValueByThemeNameLib,
      getTemplateInstance,
      getThemeSettings
    } = this.props;
    const { templateId } = this.props.match.params;

    const resultWidgetSettings = await getThemeSettings(templateId);
    const templateInstanceData = await getTemplateInstance(
      templateId,
      _.get(resultWidgetSettings, 'payload.data')
    );

    try {
      const result = await getTemplateSettingById(templateId);
      if (result.error) {
        this.setState({
          isErrorTheme: true
        });
        return;
      }
      getThemeSettingDefaultValueByThemeNameLib(
        _.get(templateInstanceData, 'payload.data.themeNameLib', '')
      );
    } catch (e) {
      console.log('ERROR_LOAD_THEME', e);
    }
  }

  toggleModal = (name: string, value: boolean) => {
    this.setState({
      [name]: value
    });
  };

  resetValue = () => {
    const { updateMultiFieldsReducer } = this.props;
    updateMultiFieldsReducer({
      firstTimeActivate: false
    });
  };

  checkLinkedWithLiveTheme = () => {
    const { themes = [], linkThemeIds } = this.props;

    let linkedWithLiveTheme = false;
    for (const themeId of linkThemeIds) {
      const themeDetails = _.find(themes, { id: themeId });
      if (!_.isEmpty(themeDetails) && themeDetails.role === 'main') {
        linkedWithLiveTheme = true;
        break;
      }
    }

    return linkedWithLiveTheme;
  };

  handleLoadingPreview = () => {
    this.loadingBar && this.loadingBar.continuousStart();
  };

  handleCompleteLoadingPreview = () => {
    this.loadingBar && this.loadingBar.complete();
    this.setState({
      prevDevice: this.props.device
    });
  };

  isDefaultThemeLib = () => {
    const { boostThemeLib } = this.props;
    return boostThemeLib === 'default';
  };

  togglePopover = () => {
    this.setState(({ activePopoverMenu }) => {
      return { activePopoverMenu: !activePopoverMenu };
    });
  };

  handleApplyThemeSetting = setting => {
    const { updateThemeSetting, updateMultiFieldsReducer, boostThemeLib } = this.props;

    // In case value viewAs of source theme does not exist in destination theme -> pick the value 2,3,4columns of list options
    const viewAsOptions = VIEW_AS_OPTIONS_BY_THEME[boostThemeLib];
    const clonedSetting = { ...setting };
    if (
      setting?.toolbar_viewAs?.listType &&
      !viewAsOptions.find(option => option.value === setting?.toolbar_viewAs?.listType)
    ) {
      clonedSetting.toolbar_viewAs.listType = viewAsOptions[0].value; // the 0 element is 2,3,4columns
    }

    // Replace the whole form -> tricky to apply the same layout
    updateThemeSetting(clonedSetting);
    updateMultiFieldsReducer({
      isFormReplaceDirty: true
    });
  };

  compileScss = async (widgetSettings, scssContent, props) => {
    if (!_.isEmpty(widgetSettings) && !_.isEmpty(scssContent)) {
      const sassMapString = `$settings: (${drill(widgetSettings)});\n`;
      const compiledScss = sassMapString + '\n' + scssContent;

      const { css } = sass.compileString(compiledScss);

      this.setState({
        compiledSettingsCss: css
      });
    }
  };

  render() {
    const {
      i18n,
      templateName,
      handleSubmit,
      history,
      touch,
      device,
      selectedPage,
      // formValues,
      templateSettingLoading,
      editThemeSettingLoading,
      getAllThemesLoading,
      getTemplateInstancesLoading,
      dirty,
      pristine,
      submitting,
      themeSetting,
      templateInstance,
      isFormReplaceDirty,
      editThemeSettingError,
      checkingTheme
    } = this.props;
    const { isErrorTheme, prevDevice } = this.state;
    const { templateId } = this.props.match.params;

    const pages = PREVIEW_PAGES_SHOPIFY_INTEGRATION.map(item => ({
      ...item,
      label: i18n.translate(item.label)
    }));

    const devices = [
      {
        id: DEVICE.DESKTOP,
        content: <Icon source={DesktopMajor} color="base" />,
        panelID: DEVICE.DESKTOP
      },
      {
        id: DEVICE.MOBILE,
        content: <Icon source={MobileMajor} color="base" />,
        panelID: DEVICE.MOBILE
      },
      {
        id: DEVICE.FULLSCREEN,
        content: <IconFullscreen />,
        panelID: DEVICE.FULLSCREEN
      }
    ];

    if (
      templateSettingLoading ||
      getAllThemesLoading ||
      getTemplateInstancesLoading ||
      checkingTheme
    ) {
      return (
        <div className="BC-ThemeSetting-Editor-Page shopify-integration-setting-visual-editor">
          <Frame>
            <Spinner accessibilityLabel="Spinner example" size="large" />
          </Frame>
        </div>
      );
    }

    if (isErrorTheme) {
      return (
        <div className="BC-Page BC-Shopify-Integration-Manual-Install">
          <div className="error-container">
            <img src={Error404} alt="404" />
            <h1 className="title">
              {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Common.templateNoExist')}
            </h1>
            <Button onClick={this.handleCloseEditor}>
              {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Common.backToIntegration')}
            </Button>
          </div>
        </div>
      );
    }

    const themePublished = this.checkLinkedWithLiveTheme();
    // console.log('formValues ------> ', formValues);
    return (
      <div className="BC-ThemeSetting-Editor-Page shopify-integration-setting-visual-editor">
        <FullscreenBar>
          <button
            className="BC-BackAction Polaris-FullscreenBar__BackAction"
            onClick={this.handleCloseEditor}>
            <Tooltip
              content={i18n.translate('AdminHTML.ThemeSetting.Common.exit')}
              zIndexOverride="520">
              <Icon source={ExitMajor} color="base" />
            </Tooltip>
          </button>
          <div className="visual-editor-header">
            <div className="theme-name">
              <p>{templateName}</p>
              {themePublished && (
                <Badge>
                  {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Common.liveTheme')}
                </Badge>
              )}
            </div>
            <div className={Classnames('theme-preview')}>
              <div className="theme-preview-label">
                <span>{i18n.translate('AdminHTML.ThemeSetting.Common.preview')}</span>
              </div>
              <div className="select-theme-preview">
                <Select
                  value={selectedPage}
                  options={pages}
                  placeholder={i18n.translate(
                    'AdminHTML.ThemeSetup.ThemeSetup.step2_Theme_Placeholder'
                  )}
                  components={{ DropdownIndicator, IndicatorSeparator: () => null }}
                  onChange={this.handleChangeSelectedPage}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused
                        ? 'rgba(0,126,98,1) !important'
                        : 'hsl(0,0%,80%)',
                      boxShadow: state.isFocused ? '0 0 0 1px rgba(0,126,98,1)' : ''
                    }),
                    option: (styles, { isFocused, isSelected }) => {
                      return {
                        ...styles,
                        backgroundColor: isSelected
                          ? 'rgba(0,126,98,1)'
                          : isFocused
                          ? '#F1F8F5'
                          : '#fff',
                        color: isSelected ? '#FFF' : '#000',
                        padding: '12px'
                      };
                    }
                  }}
                  isSearchable={false}
                />

                <ul className="Polaris-Tabs Polaris-Tabs--fillSpace">
                  {devices.map(item => {
                    return (
                      <li
                        className="Polaris-Tabs__TabContainer"
                        key={item.id}
                        onClick={() => this.handleChangeDevice(item.id)}>
                        <button
                          className={`Polaris-Tabs__Tab ${
                            device === item.id ? 'Polaris-Tabs__Tab--selected' : ''
                          }`}>
                          <span
                            className="Polaris-Tabs__Title"
                            style={item.id === DEVICE.FULLSCREEN ? { height: '36px' } : {}}>
                            {item.content}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="visual-editor-header__action">
              <ButtonGroup>
                <Button
                  primary
                  onClick={handleSubmit(this.handleSave)}
                  disabled={
                    !editThemeSettingError &&
                    (!!Object.keys(this.props.synchError).length || pristine || submitting) &&
                    // Disable when use layout library while there are some errors
                    ((isFormReplaceDirty && !!Object.keys(this.props.synchError).length) ||
                      !isFormReplaceDirty)
                  }
                  loading={editThemeSettingLoading}>
                  {i18n.translate('AdminHTML.Common.Action.save')}
                </Button>
              </ButtonGroup>
            </div>
          </div>
          <LoadingBar color="rgba(0,126,98,1)" shadow={true} ref={e => (this.loadingBar = e)} />
        </FullscreenBar>
        <div
          className={Classnames('visual-editor-body', {
            mobile: prevDevice === DEVICE.MOBILE,
            desktop: [DEVICE.DESKTOP, DEVICE.FULLSCREEN].includes(prevDevice)
          })}>
          <LeftPanel
            prevDevice={prevDevice}
            touch={touch}
            templateId={templateId}
            // isDifferentFormOrigin={!_.isEqual(formValues, originalSetting)}
            upsertElementActions={this.upsertElementActions}
          />
          {!_.isEmpty(themeSetting) &&
            !_.isEmpty(templateInstance) &&
            !!this.state.compiledSettingsCss && (
              <Preview
                compiledSettingsCss={this.state.compiledSettingsCss}
                prevDevice={prevDevice}
              />
            )}
        </div>

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty || isFormReplaceDirty}
          // Navigate function
          navigate={path => {
            if (dirty || isFormReplaceDirty) {
              const { handleOpenFullscreen, updateFieldReducer } = this.props;
              exitSABFullscreen();
              handleOpenFullscreen(false);
              updateFieldReducer('isFormReplaceDirty', false);
            }
            history.push(path);
          }}
          shouldBlockNavigation={dirty || isFormReplaceDirty}
        />
      </div>
    );
  }

  upsertElementActions = (type: string, properties: Record<string, any>) => {
    const { addedElements, removedElements } = this.state;
    const elementName = properties.elementName;
    if (type === 'add') {
      const removeExists = _.findIndex(removedElements, item => item.elementName === elementName);
      if (removeExists !== -1) removedElements.splice(removeExists, 1);
      else addedElements.push(properties);
    } else if (type === 'remove') {
      const addExists = _.findIndex(addedElements, item => item.elementName === elementName);
      if (addExists !== -1) addedElements.splice(addExists, 1);
      else removedElements.push(properties);
    }

    this.setState({ removedElements, addedElements });
  };

  // handle save settings
  handleSave = async () => {
    try {
      const { formValues, editThemeSettings, updateFieldReducer, templateName, widgetSettings } =
        this.props;
      const { templateId } = this.props.match.params;
      // const { addedElements, removedElements } = this.state;

      // Filter falsy key-value of formValues
      const truthyFormValues = {};
      Object.keys(formValues.setting).forEach(key => {
        if (formValues.setting[key]) {
          return (truthyFormValues[key] = formValues.setting[key]);
        }
      });

      const transformedData = convertToDataThemeSetting(truthyFormValues);
      const shopMetaFieldSetting = convertToShopifyThemeSetting(
        transformedData,
        formValues.pagination // Pass theme setting default value for case copying pagination button style infinite scroll from load more
      );

      const result = await editThemeSettings(
        templateId,
        {
          data: {
            ...formValues,
            setting: truthyFormValues
          },
          shopMetaFieldSetting,
          widgetSettings: {
            ...widgetSettings,
            themeSettings: shopMetaFieldSetting
          }
        }
        // files
      );

      if (!result.error) {
        // Track Full Story event
        trackFSEvent_ChangeSSRVisualEditor(templateName, templateId, {
          ...formValues,
          setting: truthyFormValues
        });
      }
      // this.setState({ addedElements: [], removedElements: [] });
      updateFieldReducer('isFormReplaceDirty', false);
    } catch (e) {
      console.log('ERROR -------> ', e);
    }
  };

  handleChangeDevice = selected => {
    this.props.changeDevice(selected);

    const { prevDevice } = this.state;
    if (
      (prevDevice === DEVICE.DESKTOP && selected === DEVICE.FULLSCREEN) ||
      (prevDevice === DEVICE.FULLSCREEN && selected === DEVICE.DESKTOP)
    ) {
      this.setState({
        prevDevice: selected
      });
    }
  };

  handleChangeSelectedPage = value => {
    const { changeSelectedPage } = this.props;
    changeSelectedPage(value);
  };

  // Handle action opening the visual editor
  handleOpenEditor = () => {
    const { handleOpenFullscreen } = this.props;
    this.unsubscribeExit = enterSABFullscreen(this.handleCloseEditor);
    handleOpenFullscreen(true);
  };

  // Handle action closing the visual editor
  handleCloseEditor = () => {
    const { handleOpenFullscreen, history, dirty, selectedThemeId } = this.props;

    if (!dirty) {
      exitSABFullscreen();
      handleOpenFullscreen(false);
    }

    let url = routes.shopifyIntegration.view();
    if (selectedThemeId) {
      url = routes.shopifyIntegration.viewThemeWidget(selectedThemeId);
    }
    const cacheRoute = getPrevRoute();
    if (cacheRoute) {
      url = cacheRoute;
      removePrevRoute();
    }
    history.push(url);
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { i18n, dirty, isFormReplaceDirty } = this.props;
    window.localStorage.removeItem(`${getShop()}-${KEY_EDITOR_STATE}`);
    return confirmLeavePage(event, dirty || isFormReplaceDirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),
  handleOpenFullscreen: (isOpenFullScreen: boolean) =>
    dispatch(handleOpenFullscreen(isOpenFullScreen)),
  changeSelectedPage: page =>
    dispatch(ShopifyIntegrationSettingsAction.handleChangeSelectedPage(page)),

  updateField: (field, value) =>
    dispatch(change(`ShopifyIntegrationSettingsVisualEditorForm`, field, value)),
  resetForm: () => dispatch(reset('ShopifyIntegrationSettingsVisualEditorForm')),
  initialize: data => dispatch(initialize('ShopifyIntegrationSettingsVisualEditorForm', data)),

  updateThemeSetting: setting =>
    dispatch(ShopifyIntegrationSettingsAction.updateThemeSetting(setting, ownProps.i18n)),
  getTemplateSettingById: id =>
    dispatch(ShopifyIntegrationSettingsAction.getTemplateSettingById(id, ownProps.i18n)),
  getThemeSettingDefaultValueByThemeNameLib: id =>
    dispatch(
      ShopifyIntegrationSettingsAction.getThemeSettingDefaultValueByThemeNameLib(id, ownProps.i18n)
    ),
  editThemeSettings: (templateId, data) =>
    dispatch(ShopifyIntegrationSettingsAction.editThemeSettings(templateId, data, ownProps.i18n)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateField(fieldName, data)),
  updateMultiFieldsReducer: data =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateMultiFields(data)),
  changeDevice: device => dispatch(ShopifyIntegrationSettingsAction.handleChangeDevice(device)),

  getAllThemes: () => dispatch(shopifyIntegrationThemeAction.getAllThemes(ownProps.i18n)),
  getTemplateInstance: (templateId, widgetSettings) =>
    dispatch(templateInstanceAction.getTemplateInstanceById(ownProps.i18n, templateId)),
  getThemeSettings: templateId =>
    dispatch(themeIntegrationAction.getThemeSettings(ownProps.i18n, templateId)),

  updateFieldTemplateInstancesWidgetsReducer: (fieldName, data) =>
    dispatch(templateInstanceAction.handleUpdateField(fieldName, data)),

  updateMultiFieldsThemeIntegrationReducer: data =>
    dispatch(themeIntegrationAction.handleUpdateMultiFields(data))
});

const mapStateToProps = (state, props) => {
  const synchError = getFormSyncErrors('ShopifyIntegrationSettingsVisualEditorForm')(state);
  const formValues = getFormValues('ShopifyIntegrationSettingsVisualEditorForm')(state) || {};
  const templateSetting = _.get(state, 'shopifyIntegrationSettings.templateSetting', {});
  const templateInstance = _.get(state, 'templateInstance', {});
  const widgetSettings = _.get(state, 'themeIntegration.widgetSettings', {});
  const scssContent = _.get(state, 'themeIntegration.scssContent', '');

  return {
    formValues,
    initialValues: { ...templateSetting },
    synchError,
    widgetSettings,
    scssContent,
    themes: _.get(state, 'shopifyIntegrationTheme.allThemes', []),
    getAllThemesLoading: _.get(state, 'shopifyIntegrationTheme.getAllThemesLoading', []),
    activeMenuItem: _.get(state, 'shopifyIntegrationSettings.activeMenuItem', ''),
    templateName: _.get(state, 'shopifyIntegrationSettings.templateName', ''),
    device: _.get(state, 'shopifyIntegrationSettings.device', DEVICE.DESKTOP),
    selectedPage: _.get(state, 'shopifyIntegrationSettings.selectedPage', ''),
    linkThemeIds: _.get(state, 'shopifyIntegrationSettings.linkThemeIds', []),
    themeSetting: _.get(state, 'shopifyIntegrationSettings.templateSetting.setting', {}),
    templateSettingLoading: _.get(
      state,
      'shopifyIntegrationSettings.templateSettingLoading',
      false
    ),
    themeSettingDefaultValueLoading: _.get(
      state,
      'shopifyIntegrationSettings.themeSettingDefaultValueLoading',
      false
    ),
    editThemeSettingLoading: _.get(
      state,
      'shopifyIntegrationSettings.editThemeSettingLoading',
      false
    ),
    editThemeSettingError: _.get(
      state,
      'shopifyIntegrationSettings.editThemeSettingError',
      undefined
    ),
    themeSettingDefaultValue: _.get(
      state,
      'shopifyIntegrationSettings.themeSettingDefaultValue',
      {}
    ),
    themeSettingDefaultThemeValue: _.get(
      state,
      'shopifyIntegrationSettings.themeSettingDefaultThemeValue',
      {}
    ),
    templateInstance: _.get(templateInstance, 'templateInstance', {}),
    getTemplateInstancesLoading: _.get(templateInstance, 'getTemplateInstancesLoading', {}),

    isFormReplaceDirty: _.get(state, 'shopifyIntegrationSettings.isFormReplaceDirty', false),
    firstTimeActivate: _.get(state, 'shopifyIntegrationSettings.firstTimeActivate', false),
    labelTranslation: _.get(state, 'shopifyIntegrationSettings.labelTranslation', {}),
    boostThemeLib: _.get(state, 'shopifyIntegrationSettings.boostThemeLib', 'default'),
    checkingTheme: _.get(state, 'themeIntegration.checkingTheme', false),
    selectedThemeId: _.get(state, 'themeIntegration.selectedThemeId', '')
  };
};

const validate = values => {
  const errors = {};
  const typography = _.get(values, 'typography', {});
  Object.keys(typography).forEach(key => {
    const typo = typography[key];
    const fontSizeScale = _.get(typo, 'fontSizeScale');
    const fontSizeScaleRange = _.get(typo, 'fontSizeScaleRange', []);
    if (fontSizeScale < fontSizeScaleRange[0]) {
      _.set(
        errors,
        `typography.${key}fontSizeScale`,
        `Please enter a value more than or equal to ${fontSizeScaleRange[0]}`
      );
    }
  });

  return errors;
};

const ShopifyIntegrationSettingsVisualEditorForm = reduxForm({
  form: 'ShopifyIntegrationSettingsVisualEditorForm', // a unique identifier for this form
  enableReinitialize: true,
  validate: validate
  // keepDirtyOnReinitialize: true
})(ShopifyIntegrationSettingsVisualEditor);

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(ShopifyIntegrationSettingsVisualEditorForm)
);

const DropdownIndicator = props => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <Icon source={CaretDownMinor} color="base" />
      </components.DropdownIndicator>
    )
  );
};
